import { useState, useEffect } from "react";
import { useLang } from "@/context/LanguageContext";
import translations from "@/data/translations";
import { Menu, X, MessageCircle, Globe } from "lucide-react";

const WHATSAPP_URL = "https://wa.me/34672808030";

const Navigation = () => {
  const { lang, toggleLang } = useLang();
  const t = translations[lang].nav;
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setMobileOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const links = [
    { label: t.services, id: "services" },
    { label: t.howItWorks, id: "how-it-works" },
    { label: t.whyUs, id: "value-proposition" },
    { label: t.pricing, id: "pricing" },
    { label: t.compare, id: "comparison" },
  ];

  return (
    <nav
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "nav-glass-scrolled" : "nav-glass"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 flex items-center justify-between h-28 md:h-[116px]">
        {/* Logo */}
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-2 shrink-0"
        >
          <img
            src="/logo.png"
            alt="OKontainers"
            className="h-[86px] md:h-[108px] w-auto object-contain"
          />
        </button>

        {/* Desktop links */}
        <div className="hidden lg:flex items-center gap-7">
          {links.map((link) => (
            <button
              key={link.id}
              data-testid={`nav-link-${link.id}`}
              onClick={() => scrollTo(link.id)}
              className="text-sm font-medium text-slate-600 hover:text-navy transition-colors duration-200 font-body"
            >
              {link.label}
            </button>
          ))}
        </div>

        {/* Right section */}
        <div className="flex items-center gap-3">
          <button
            data-testid="lang-toggle"
            onClick={toggleLang}
            className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-navy transition-colors px-3 py-1.5 rounded-full border border-slate-200 hover:border-slate-300"
          >
            <Globe size={14} />
            {lang === "es" ? "EN" : "ES"}
          </button>

          <a
            data-testid="nav-cta-whatsapp"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-flex items-center gap-2 bg-electric text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-electric-hover transition-all duration-300 shadow-lg shadow-electric/20 hover:shadow-electric/40"
          >
            <MessageCircle size={16} />
            {t.cta}
          </a>

          {/* Mobile menu toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2 text-navy"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-t border-slate-100 px-5 py-6 space-y-1"
        >
          {links.map((link) => (
            <button
              key={link.id}
              data-testid={`mobile-link-${link.id}`}
              onClick={() => scrollTo(link.id)}
              className="block w-full text-left text-base font-medium text-slate-700 hover:text-electric py-3 px-2 rounded-lg hover:bg-slate-50 transition-colors"
            >
              {link.label}
            </button>
          ))}
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 mt-4 bg-electric text-white font-semibold py-3 px-6 rounded-full hover:bg-electric-hover transition-all"
          >
            <MessageCircle size={18} />
            {t.cta}
          </a>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
